import type { DataViewId } from 'cadenza/workbook/model/data-view-id';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { Prefix } from 'cadenza/utils/type-util';
import type { WorkbookLayerStatus } from 'cadenza/workbook/model/workbook-layer-status';
import type { AttributeStatus } from 'cadenza/repository/model/attribute-status';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { assert } from 'cadenza/utils/custom-error';
import type { Opaque } from 'cadenza/utils/opaque';
import { uniqueId } from 'cadenza/utils/unique-id';
import type { CollapsedNotification } from 'cadenza/workbook/view/view-header/view-notification-bar';

import i18n from './notifications.properties';

export type WorkbookNotificationId = Opaque<'NotificationId', string>;

export type NotificationLevel =
  | 'FATAL' // Fatal means the view could not be loaded at all. These notifications are shown directly in the view content
  | 'ERROR'
  | 'WARNING'
  | 'INFO'
  | 'CUSTOM'; // Custom notifications are handled only in some specific code and should not be shown in the "standard" places

const NOTIFICATION_SEVERITY_WEIGHTS = {
  FATAL: 0,
  ERROR: 1,
  WARNING: 2,
  INFO: 3,
  CUSTOM: 4,
} as const;

// Use this comparator sort notifications by severity
export function compareNotificationsBySeverity(
  one: BaseNotification | CollapsedNotification,
  another: BaseNotification | CollapsedNotification,
) {
  const a = (one as CollapsedNotification).notification ?? one;
  const b = (another as CollapsedNotification).notification ?? another;
  return NOTIFICATION_SEVERITY_WEIGHTS[a.level] - NOTIFICATION_SEVERITY_WEIGHTS[b.level];
}

// From where a notification was added. Useful when updating, to replace only certain notifications.
export type NotificationSource = 'WORKBOOK_UPDATE' | 'VIEW';

/**
 * Represents an action that can be presented (as a link or button) to the user,
 * together with the notification message.
 */
interface NotificationAction {
  name: 'CHANGE_WMS_LAYER_CREDENTIALS'; // currently only one use case
  label: string;
  params?: Record<string, unknown>;
}

// Common notification fields
interface BaseNotification {
  id: WorkbookNotificationId;
  level: NotificationLevel;
  source: NotificationSource;
  viewId: ViewId;
  title?: string;
  detail?: string;
  action?: NotificationAction;
}

// The type 'GENERIC' is for notifications that are created by the view with messages and details
// Eventually we might centralize their i18n and add options for parameters in 18n texts.
type WorkbookViewNotificationType = 'GENERIC' | 'VIEW_LOAD_ERROR';

/**
 * Notification attached to a certain view.
 */
interface WorkbookViewNotification extends BaseNotification {
  type: WorkbookViewNotificationType;
}

export type DataViewNotificationType =
  | 'DATA_QUERY_TIMEOUT'
  | 'MISSING_ATTRIBUTE'
  | 'INVALID_VISUALIZATION'
  | 'UNKNOWN_VISUALIZATION'
  | 'INVALID_COLOR_PALETTE'
  | 'INVALID_COLOR_GRADIENT'
  | 'CHART_VISUALIZATION_LIMITED'
  | 'CHART_DATA_DISPLAY_LIMITED'
  | 'CHART_LOGARITHMIC_SCALE_NOT_APPLIED'
  | 'INVALID_COLOR_CLASSIFICATION'
  | 'TABLE_STYLING_TEMPLATE_MISSING'
  | Prefix<'INVALID_ATTRIBUTE.', Exclude<AttributeStatus, 'OK'>>;

/**
 * Notification attached to a certain data view within a view.
 */
export interface DataViewNotification extends BaseNotification {
  type: DataViewNotificationType;
  dataViewId: DataViewId;
}

type LayerNotificationType =
  | 'OUT_OF_SCALE_RANGE'
  | 'LAYER_UNAUTHENTICATED'
  | 'LAYER_NON_EXISTING'
  | 'LAYER_INITIALIZATION_FAILED'
  | 'LAYER_DATA_LOADING_FAILED'
  | 'LAYER_RESULT_TRUNCATED_BY_ROWCOUNT'
  | 'LAYER_RESULT_TRUNCATED_BY_GEOMETRY_VERTEX_COUNT'
  | 'LAYER_RESULT_TRUNCATED_BY_ROWCOUNT_UNFILTERABLE'
  | 'LAYER_RESULT_TRUNCATED_BY_GEOMETRY_VERTEX_COUNT_UNFILTERABLE'
  | 'LAYER_SRS_DIFFERS_FROM_MAP'
  | Exclude<WorkbookLayerStatus, 'OK'>;

/**
 * Notification attached to a certain map layer within a map view.
 */
export interface LayerNotification extends BaseNotification {
  type: LayerNotificationType;
  layerId: LayerId;
}

export type WorkbookNotification = WorkbookViewNotification | DataViewNotification | LayerNotification;

export type NotificationType = DataViewNotificationType | LayerNotificationType | WorkbookViewNotificationType;

// Extension point to add more actions: add new name here
type NotificationActionName = 'CHANGE_WMS_LAYER_CREDENTIALS';

// Defines which notification type has an associated action
const ACTIONS_BY_NOTIFICATION_TYPE: Partial<Record<NotificationType, NotificationActionName>> = {
  LAYER_UNAUTHENTICATED: 'CHANGE_WMS_LAYER_CREDENTIALS',
};

export function isDataViewNotification(notification: WorkbookNotification): notification is DataViewNotification {
  return Boolean((notification as DataViewNotification).dataViewId);
}

export function isLayerNotification(notification: WorkbookNotification): notification is LayerNotification {
  return Boolean((notification as LayerNotification).layerId);
}

/**
 * Create a workbook notification.
 * The ID of the view to which this notification is associated should always be provided.
 * Optionally, one of layerId and dataViewId may be provided, if the notification is specifically associated to a data layer or a view.
 * The title and details text might be provided. If they are not a title and detail text will be resolved by i18n based on the notification type.
 * The function assigns a random unique ID to each notification it creates.
 *
 * Always use this function to create notifications to avoid problems and keep it maintainable!
 *
 * @param severity
 * @param source
 * @param type
 * @param viewId
 * @param dataViewId
 * @param layerId
 * @param title - Title text
 * @param detail - Detailed text
 * @param detailParams - Parameters to be passed to the i18n when resolving the detail text
 * @param actionParams - Parameters to be passed to the action executor
 */
export function createNotification(
  severity: NotificationLevel,
  source: NotificationSource,
  type: NotificationType,
  viewId: ViewId,
  {
    dataViewId,
    layerId,
    title,
    detail,
    detailParams,
    actionParams,
  }: {
    dataViewId?: DataViewId;
    layerId?: LayerId;
    title?: string;
    detail?: string;
    detailParams?: Record<string, string>;
    actionParams?: Record<string, unknown>;
  } = {},
): WorkbookViewNotification | LayerNotification | DataViewNotification {
  assert(!(dataViewId && layerId), 'Only one of dataViewId and layerId might be defined.');

  const notification = {
    id: uniqueId() as WorkbookNotificationId,
    type,
    source,
    level: severity,
    title: title ?? i18n(`notification.<${type}>.title`),
    detail: detail ?? i18n(`notification.<${type}>.detail`, detailParams),
    action: createAction(type, actionParams),
  };
  if (layerId != null) {
    return { ...notification, type: type as LayerNotificationType, layerId, viewId };
  }
  if (dataViewId != null) {
    return { ...notification, type: type as DataViewNotificationType, dataViewId: dataViewId!, viewId };
  }
  return { ...notification, type: type as WorkbookViewNotificationType, viewId };
}

function createAction(
  notificationType: NotificationType,
  actionParams?: Record<string, unknown>,
): NotificationAction | undefined {
  const actionName = ACTIONS_BY_NOTIFICATION_TYPE[notificationType];
  if (actionName) {
    return {
      name: actionName,
      label: i18n(`notification.<${notificationType}>.action`),
      params: actionParams,
    };
  }
}
